import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FORM_SECTIONS, RISK_SORULARI } from "@/lib/formConfig";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, Save } from "lucide-react";

function Field({ f, value, onChange }) {
  const span = f.span === 3 ? "md:col-span-3" : f.span === 2 ? "md:col-span-2" : "";
  const cls = "rounded-none bg-zinc-900 border-zinc-800 focus-visible:ring-white";

  if (f.type === "bool") {
    return (
      <div className={`flex items-center gap-2.5 border border-zinc-800 bg-zinc-900 px-3 py-2.5 ${span}`}>
        <Checkbox
          id={f.key}
          data-testid={`field-${f.key}`}
          checked={!!value}
          onCheckedChange={(v) => onChange(f.key, !!v)}
        />
        <Label htmlFor={f.key} className="text-sm cursor-pointer font-normal">{f.label}</Label>
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 ${span}`}>
      <Label className="text-xs text-zinc-400">{f.label}{f.required && <span className="text-red-400"> *</span>}</Label>
      {f.type === "text" && (
        <Input data-testid={`field-${f.key}`} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} className={cls} />
      )}
      {f.type === "number" && (
        <Input type="number" data-testid={`field-${f.key}`} value={value ?? ""} onChange={(e) => onChange(f.key, e.target.value === "" ? "" : Number(e.target.value))} className={cls} />
      )}
      {f.type === "date" && (
        <Input type="date" data-testid={`field-${f.key}`} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} className={cls} />
      )}
      {f.type === "textarea" && (
        <Textarea data-testid={`field-${f.key}`} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} className={cls} rows={2} />
      )}
      {(f.type === "select" || f.type === "varyok") && (
        <Select value={value || ""} onValueChange={(v) => onChange(f.key, v)}>
          <SelectTrigger data-testid={`field-${f.key}`} className={cls}><SelectValue placeholder="Seçiniz" /></SelectTrigger>
          <SelectContent className="rounded-none bg-zinc-900 border-zinc-800">
            {(f.type === "varyok" ? ["Var", "Yok"] : f.options).map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {f.type === "multi" && (
        <div className="flex flex-wrap gap-2 pt-1">
          {f.options.map((o) => {
            const arr = Array.isArray(value) ? value : [];
            const on = arr.includes(o);
            return (
              <button
                type="button"
                key={o}
                data-testid={`field-${f.key}-${o}`}
                onClick={() => onChange(f.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                className={`px-3 py-1.5 text-xs border transition-colors ${on ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}
              >
                {o}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CustomerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [kart, setKart] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api.get(`/customers/${id}`).then((r) => setKart(r.data.kart || {}));
  }, [id]);

  const set = (key, val) => setKart((p) => ({ ...p, [key]: val }));

  const save = async () => {
    if (!kart.unvan || !kart.tur) {
      toast.error("Unvan ve mükellef türü zorunludur.");
      return;
    }
    setSaving(true);
    try {
      if (id) {
        await api.put(`/customers/${id}`, { kart, durum: "aktif" });
        toast.success("Müşteri güncellendi.");
        navigate(`/musteriler/${id}`);
      } else {
        const r = await api.post("/customers", { kart, durum: "aktif" });
        toast.success("Müşteri oluşturuldu.");
        navigate(`/musteriler/${r.data.id}`);
      }
    } catch (e) {
      toast.error("Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-[1100px]">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4" data-testid="form-back-btn">
        <ArrowLeft className="w-4 h-4" /> Geri
      </button>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{id ? "Müşteri Düzenle" : "Yeni Müşteri Kartı"}</h1>
          <p className="text-zinc-500 text-sm mt-1">Kabul ve bilgi kartı — bölümleri doldurun</p>
        </div>
        <Button data-testid="save-customer-btn" onClick={save} disabled={saving} className="rounded-none bg-white text-black hover:bg-zinc-200 font-semibold">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["A", "B", "H"]} className="space-y-3">
        {FORM_SECTIONS.map((sec) => (
          <AccordionItem key={sec.id} value={sec.id} className="border border-zinc-800 bg-zinc-950 px-4">
            <AccordionTrigger className="hover:no-underline py-4" data-testid={`section-${sec.id}`}>
              <span className="text-sm font-semibold text-left">{sec.title}</span>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              {sec.risk ? (
                <div className="space-y-2">
                  {RISK_SORULARI.map((q) => (
                    <div key={q.key} className="flex items-center justify-between gap-4 border border-zinc-800 bg-zinc-900 px-3 py-2.5">
                      <span className="text-sm text-zinc-300">{q.label}</span>
                      <div className="flex gap-1 shrink-0">
                        {["Evet", "Hayır"].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`risk-${q.key}-${opt}`}
                            onClick={() => set(q.key, opt)}
                            className={`px-3 py-1 text-xs border transition-colors ${kart[q.key] === opt ? (opt === "Evet" ? "bg-red-500 text-white border-red-500" : "bg-green-500 text-white border-green-500") : "border-zinc-700 text-zinc-400 hover:text-white"}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {sec.fields.map((f) => (
                      <Field key={f.key} f={f} value={kart[f.key]} onChange={set} />
                    ))}
                  </div>
                  {sec.checklist && (
                    <div className="mt-4 pt-4 border-t border-zinc-800">
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Devir Kontrol Listesi</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {sec.checklist.map((c) => (
                          <div key={c.key} className="flex items-center gap-2.5 border border-zinc-800 bg-zinc-900 px-3 py-2">
                            <Checkbox id={c.key} data-testid={`field-${c.key}`} checked={!!kart[c.key]} onCheckedChange={(v) => set(c.key, !!v)} />
                            <Label htmlFor={c.key} className="text-sm cursor-pointer font-normal">{c.label}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="flex justify-end mt-6">
        <Button data-testid="save-customer-btn-bottom" onClick={save} disabled={saving} className="rounded-none bg-white text-black hover:bg-zinc-200 font-semibold">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </div>
    </div>
  );
}
